import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  noindex?: boolean;
  nofollow?: boolean;
  jsonLd?: object;
}

const BASE_URL = 'https://znajdki.pl';
const DEFAULT_IMAGE = '/photos/logo/logo.png';

export function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  noindex = false,
  nofollow = false,
  jsonLd,
}: SEOProps) {
  const fullTitle = title.includes('Fundacja') ? title : `${title} | Fundacja "Znajdki"`;
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  const imageUrl = image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : `${BASE_URL}${DEFAULT_IMAGE}`;
  
  // Meta robots tag
  const robots = [];
  if (noindex) robots.push('noindex');
  if (nofollow) robots.push('nofollow');
  if (robots.length === 0) robots.push('index', 'follow');

  // Clean up any existing canonical links before React Helmet adds the new one
  React.useEffect(() => {
    const existingCanonical = document.querySelectorAll('link[rel="canonical"]');
    existingCanonical.forEach(link => link.remove());
  }, [fullUrl]);

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        
        {/* Robots */}
        <meta name="robots" content={robots.join(', ')} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content={type} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:site_name" content="Fundacja Znajdki" />
        <meta property="og:locale" content="pl_PL" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={fullUrl} />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Fundacja Znajdki" />
        <meta name="language" content="Polish" />
        <meta name="theme-color" content="#C41E3A" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      </Helmet>
      
      {/* Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      
      {/* Default Organization Schema */}
      {!jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            'name': 'Fundacja Znajdki',
            'legalName': 'Fundacja „Znajdki"',
            'url': 'https://znajdki.pl',
            'logo': 'https://znajdki.pl/photos/logo/logo.png',
            'taxID': '1132860384',
            'vatID': 'PL1132860384',
            'identifier': [
              { '@type': 'PropertyValue', 'propertyID': 'KRS', 'value': '0000437387' },
              { '@type': 'PropertyValue', 'propertyID': 'NIP', 'value': '1132860384' },
              { '@type': 'PropertyValue', 'propertyID': 'REGON', 'value': '146390781' }
            ],
            'sameAs': [
              'https://www.facebook.com/znajdki/',
              'https://www.instagram.com/fundacja_znajdki/'
            ],
            'contactPoint': {
              '@type': 'ContactPoint',
              'telephone': '+48-664-821-160',
              'contactType': 'customer service',
              'email': 'znajdki2012@gmail.com'
            },
            'address': {
              '@type': 'PostalAddress',
              'streetAddress': 'ul. Leona Berensona 90',
              'addressLocality': 'Warszawa',
              'addressRegion': 'Mazowieckie',
              'postalCode': '03-287',
              'addressCountry': 'PL'
            }
          })}
        </script>
      )}
    </>
  );
}

